import { z } from 'zod'
import { AsyncFn } from '../../common'
import { guidSchema } from '../../contracts'
import { bzOptional } from '../common-schemas'

// Report Categories - high-level grouping
export const EMBEDDED_REPORT_CATEGORIES = ['COMPANY', 'TECHNICIAN', 'CSR'] as const
export type EmbeddedReportCategory = (typeof EMBEDDED_REPORT_CATEGORIES)[number]

// Report Types - high-level grouping
export const EMBEDDED_REPORT_TYPES = ['EXPORT', 'DASHBOARD', 'INTERACTIVE_QUESTION'] as const
export type EmbeddedReportType = (typeof EMBEDDED_REPORT_TYPES)[number]

export const EMBEDDED_REPORT_ACCESS_CONTROL_TYPES = ['GLOBAL', 'OPT_IN'] as const
export type EmbeddedReportAccessControlType = (typeof EMBEDDED_REPORT_ACCESS_CONTROL_TYPES)[number]
export const EmbeddedReportAccessControlTypeDisplayNames = {
  GLOBAL: 'Global',
  OPT_IN: 'Opt-In',
} as const

export const EmbeddedReportTypeDisplayNames = {
  EXPORT: 'Export Table',
  DASHBOARD: 'Dashboard',
  INTERACTIVE_QUESTION: 'Interactive Question',
} as const

const EmbeddedReportConfigBaseSchema = z.object({
  reportGuid: guidSchema,
  reportCategory: z.enum(EMBEDDED_REPORT_CATEGORIES),
  reportType: z.enum(EMBEDDED_REPORT_TYPES),
  reportTitle: z.string(),
  reportDescription: bzOptional(z.string()),
  externalReportId: z.string(),
})

export type EmbeddedReportBaseConfig = z.infer<typeof EmbeddedReportConfigBaseSchema>

// Type-specific Report Interfaces
export interface ExportReportConfig extends EmbeddedReportBaseConfig {
  reportType: 'EXPORT'
}

export interface InteractiveQuestionReportConfig extends EmbeddedReportBaseConfig {
  reportType: 'INTERACTIVE_QUESTION'
}

export interface DashboardReportConfig extends EmbeddedReportBaseConfig {
  reportType: 'DASHBOARD'
}

// Union type for all report configurations
export type EmbeddedReportConfig = ExportReportConfig | DashboardReportConfig | InteractiveQuestionReportConfig

// Helper function to create a question export report
export const createExportReport = (params: Omit<ExportReportConfig, 'reportType'>): ExportReportConfig => ({
  reportType: 'EXPORT',
  ...params,
})

// Helper function to create a dashboard report
export const createDashboardReport = (params: Omit<DashboardReportConfig, 'reportType'>): DashboardReportConfig => ({
  reportType: 'DASHBOARD',
  ...params,
})

// Helper function to create an interactive question report
export const createInteractiveQuestionReport = (
  params: Omit<InteractiveQuestionReportConfig, 'reportType'>,
): InteractiveQuestionReportConfig => ({
  reportType: 'INTERACTIVE_QUESTION',
  ...params,
})

export const EmbeddedReportCategoryDisplayNames = {
  COMPANY: 'Company Reports',
  TECHNICIAN: 'Technician Reports',
  CSR: 'CSR Reports',
} as const

export const CreateEmbeddedReportDefinitionRequestSchema = z
  .object({
    reportCategory: z.enum(EMBEDDED_REPORT_CATEGORIES),
    reportType: z.enum(EMBEDDED_REPORT_TYPES),
    reportTitle: z.string(),
    reportDescription: bzOptional(z.string()),
    externalReportId: z.string(),
    accessControlType: z.enum(EMBEDDED_REPORT_ACCESS_CONTROL_TYPES),
    companyGuids: z.array(guidSchema),
  })
  .refine(
    data => {
      if (data.accessControlType === 'GLOBAL') {
        return data.companyGuids.length === 0
      }
      return data.companyGuids.length > 0
    },
    {
      message: 'Company guids must be empty if access control type is GLOBAL',
    },
  )
export type CreateEmbeddedReportDefinitionRequest = z.infer<typeof CreateEmbeddedReportDefinitionRequestSchema>

export const AssignEmbeddedReportToCompaniesRequestSchema = z.object({
  reportGuids: z.array(guidSchema).min(1, 'At least one report must be selected'),
  companyGuids: z.array(guidSchema).min(1, 'At least one company must be selected'),
})
export type AssignEmbeddedReportToCompaniesRequest = z.infer<typeof AssignEmbeddedReportToCompaniesRequestSchema>

export type EmbeddedReportDefinitionRequest =
  | {
      type: 'create'
      input: CreateEmbeddedReportDefinitionRequest
    }
  | {
      type: 'assign'
      input: AssignEmbeddedReportToCompaniesRequest
    }

export type EmbeddedReportDefinitionWriter = AsyncFn<EmbeddedReportDefinitionRequest>
